import { EDemembrementType } from "common/enums/ProductSubscribed/EDemembrementType";
import { ESubscriptionProductType } from "common/enums/ProductSubscribed/ESubscriptionProductType";
import ProductSheetResponseResource from "common/resources/ProductSheet/ProductSheetResponseResource";
import Table from "components/elements/Table";
import Typography, { ITypo } from "components/elements/Typography";
import InputElement, { EInputType } from "components/materials/Form/InputElement";
import MuiSelectInputElement, { IMuiSelectOption } from "components/materials/Form/MuiSelectInputElement";
import I18n from "components/materials/I18n";
import { useMemo } from "react";
import { getViagerRatio } from "utils/baremeViager";
import classes from "./classes.module.scss";

type IProps = {
	product: ProductSheetResponseResource;
	subscriptionProductType: ESubscriptionProductType;
	demembrementType: EDemembrementType;
	onDemembrementTypeChange: (type: EDemembrementType) => void;
	demembrementDuration: number | null;
	onDurationChange: (duration: number | null) => void;
	viagerAge: number | null;
	onViagerAgeChange: (age: number | null) => void;
	contrepartieChoice: "avec" | "sans" | null;
	onContrepartieChoiceChange: (choice: "avec" | "sans") => void;
	contrepartieNom: string | null;
	onContrepartieNomChange: (nom: string | null) => void;
	allowViager?: boolean;
};

export default function DemembrementConfigTable({
	product,
	subscriptionProductType,
	demembrementType,
	onDemembrementTypeChange,
	demembrementDuration,
	onDurationChange,
	viagerAge,
	onViagerAgeChange,
	contrepartieChoice,
	onContrepartieChoiceChange,
	contrepartieNom,
	onContrepartieNomChange,
	allowViager = true,
}: IProps) {
	const typeOptions: IMuiSelectOption[] = [
		{ id: EDemembrementType.temporaire, label: I18n.asset.pages.subscriptions.select_products.demembrement_config.type_temporaire },
		{ id: EDemembrementType.viager, label: I18n.asset.pages.subscriptions.select_products.demembrement_config.type_viager },
	];

	const durationOptions: IMuiSelectOption[] = useMemo(
		() =>
			(product.demembrementKeys ?? []).map((k) => ({
				id: k.duration,
				label: String(k.duration),
			})),
		[product],
	);

	const ratio = useMemo(() => {
		if (demembrementType === EDemembrementType.viager) {
			if (viagerAge === null) return null;
			return getViagerRatio(viagerAge, subscriptionProductType);
		}
		const key = product.demembrementKeys?.find((k) => k.duration === demembrementDuration) ?? null;
		if (!key) return null;
		return subscriptionProductType === ESubscriptionProductType.usufruit ? key.usufruitRatio : key.nueProprieteRatio;
	}, [demembrementType, viagerAge, subscriptionProductType, product, demembrementDuration]);

	const ratioDisplay = ratio !== null ? `${(ratio * 100).toFixed(2)} %` : "—";

	return (
		<div className={classes["root"]}>
			<Typography typo={ITypo.P_MEDIUM_BOLD}>{I18n.asset.pages.subscriptions.select_products.demembrement_config.title}</Typography>
			{demembrementType === EDemembrementType.viager && (
				<p>Indiquez l'âge de la personne qui détient l'usufruit, même si ce dossier concerne un autre souscripteur. Le conseiller doit confirmer cet âge avant de poursuivre.</p>
			)}
			<Table
				onNext={null}
				searchbarDisabled
				columnsHead={[
					{ key: "type", headContent: I18n.asset.pages.subscriptions.select_products.demembrement_config.type, widthPercentage: 15 },
					{ key: "durationOrAge", headContent: demembrementType === EDemembrementType.viager ? I18n.asset.pages.subscriptions.select_products.demembrement_config.viager_age : I18n.asset.pages.subscriptions.select_products.demembrement_config.duration, widthPercentage: 20 },
					{ key: "repartitionKey", headContent: I18n.asset.pages.subscriptions.select_products.demembrement_config.repartition_key, widthPercentage: 15 },
					{ key: "contrepartie", headContent: I18n.asset.pages.subscriptions.select_products.demembrement_config.contrepartie_label, widthPercentage: 12 },
					{ key: "contrepartieNom", headContent: I18n.asset.pages.subscriptions.select_products.demembrement_config.contrepartie_nom, widthPercentage: 20 },
					{ key: "link", headContent: "", widthPercentage: 10 },
				]}
				rows={[
					{
						rowKey: "config",
						type: (
							<MuiSelectInputElement
								name="demembrementType"
								options={typeOptions}
								defaultValue={{ id: demembrementType, label: I18n.asset.pages.subscriptions.select_products.demembrement_config[`type_${demembrementType}`] }}
								onChange={(_event, values) => {
									if (values) onDemembrementTypeChange(values["id"] as EDemembrementType);
								}}
								disableClearable
								disabled={!allowViager}
							/>
						),
						durationOrAge:
							demembrementType === EDemembrementType.viager ? (
								<InputElement
									name="viagerAge"
									type={EInputType.NUMBER}
									defaultValue={viagerAge ?? undefined}
									min={0}
									max={150}
									onChange={(e) => onViagerAgeChange(e.target.value ? parseInt(e.target.value) : null)}
								/>
							) : (
								<MuiSelectInputElement
									name="demembrementDuration"
									options={durationOptions}
									defaultValue={demembrementDuration ? { id: demembrementDuration, label: String(demembrementDuration) } : null}
									onChange={(_event, values) => {
										onDurationChange(values ? Number(values["id"]) : null);
									}}
								/>
							),
						repartitionKey: ratioDisplay,
						contrepartie: (
							<div className={classes["contrepartie-cell"]}>
								<label className={classes["radio-option"]}>
									<input
										type="radio"
										name="contrepartieChoice"
										value="sans"
										checked={contrepartieChoice === "sans"}
										onChange={() => {
											onContrepartieChoiceChange("sans");
											onContrepartieNomChange(null);
										}}
									/>
									<span>{I18n.asset.pages.subscriptions.select_products.demembrement_config.contrepartie_sans}</span>
								</label>
								<label className={classes["radio-option"]}>
									<input
										type="radio"
										name="contrepartieChoice"
										value="avec"
										checked={contrepartieChoice === "avec"}
										onChange={() => onContrepartieChoiceChange("avec")}
									/>
									<span>{I18n.asset.pages.subscriptions.select_products.demembrement_config.contrepartie_avec}</span>
								</label>
										</div>
						),
						contrepartieNom: contrepartieChoice === "avec" ? (
							<InputElement
								type={EInputType.TEXT}
								name="contrepartieNom"
								defaultValue={contrepartieNom ?? ""}
								onChange={(e) => onContrepartieNomChange(e.target.value || null)}
							/>
						) : null,
						link:
							demembrementType === EDemembrementType.viager ? null : (
								<a href={`/products/${product.productId}`} target="_blank" rel="noopener noreferrer" className={classes["access-link"]}>
									{I18n.asset.pages.subscriptions.select_products.demembrement_config.access_keys} →
								</a>
							),
					},
				]}
			/>
		</div>
	);
}
